/**
 * El modo soporte en el panel · `endpoints.md` §A.3, «Ver el club con sus ojos».
 *
 * Una persona del equipo de Kiap con una entrada en soporte viva abre el panel del
 * club **como su dueño**: ve lo mismo que él. Nace de **solo lectura**; para cambiar
 * algo se pulsa «Entrar en modo escritura», y entonces **cada cambio pide
 * confirmación** antes de salir hacia el servidor. Lo pidió Alejandro el 05-11-2026:
 * «que cuando vaya a hacer un cambio pida confirmación de que lo quiere hacer, para
 * no tocar nada por equivocación».
 *
 * ## La confirmación vive en el `fetch`, no en cada pantalla
 *
 * El panel tiene cientos de botones de guardar repartidos en sesenta pantallas.
 * Poner una confirmación en cada uno sería olvidar alguno, y el que se olvide es el
 * que borra algo. Así que hay **un solo sitio** por el que pasa todo lo que escribe:
 * el `fetch` de Eden (`client.ts`). Si hay una entrada en soporte con escritura y la
 * petición cambia algo del club, se para, pregunta y solo sigue con un sí.
 *
 * ## Estado de módulo, como el token
 *
 * Lo lee el cliente HTTP, que no es React, igual que `tokenDeAcceso()` en
 * `sesion.ts`. Las pantallas lo leen con los ganchos de abajo.
 *
 * ## Qué entrada, entre pestañas y recargas
 *
 * La entrada elegida se recuerda en `sessionStorage`: es de esta pestaña, se va al
 * cerrarla y sobrevive a una recarga. Si el navegador no deja guardarla, se sigue
 * funcionando: solo se pierde al recargar, y se vuelve a pulsar «Abrir su panel».
 */
import { useSyncExternalStore } from 'react';
import type { Peticion } from '@/shared/api/sesion';

/** La entrada en soporte abierta en esta pestaña, tal como la usa el panel. */
export interface EntradaEnSoporte {
  readonly id: string;
  /** El `academy_id` del club. */
  readonly club: string;
  readonly nombre: string;
  readonly motivo: string;
  readonly expira: Date;
  /** «Entrar en modo escritura» pulsado. */
  readonly escritura: boolean;
  /** `academies.status`: para decir «suspendido» como lo vería el club. */
  readonly estadoDelClub: string;
}

/* ── La entrada abierta ─────────────────────────────────────────────────── */

let actual: EntradaEnSoporte | null = null;
const oyentes = new Set<() => void>();

const avisar = () => {
  for (const fn of oyentes) fn();
};

const suscribir = (fn: () => void) => {
  oyentes.add(fn);
  return () => {
    oyentes.delete(fn);
  };
};

export const entradaEnSoporte = (): EntradaEnSoporte | null => actual;

export const fijarEntradaEnSoporte = (e: EntradaEnSoporte | null): void => {
  actual = e;
  avisar();
};

export const useEntradaEnSoporte = (): EntradaEnSoporte | null =>
  useSyncExternalStore(suscribir, entradaEnSoporte, entradaEnSoporte);

/* ── Qué entrada abrir en esta pestaña ──────────────────────────────────── */

const CLAVE = 'kiap.soporte';

export const recordarEntrada = (id: string): void => {
  try {
    sessionStorage.setItem(CLAVE, id);
  } catch {
    // Sin almacén se sigue: solo se pierde al recargar.
  }
};

export const olvidarEntrada = (): void => {
  try {
    sessionStorage.removeItem(CLAVE);
  } catch {
    // Igual que arriba.
  }
};

export const entradaRecordada = (): string | null => {
  try {
    return sessionStorage.getItem(CLAVE);
  } catch {
    return null;
  }
};

/* ── La confirmación de cada cambio ─────────────────────────────────────── */

/** Lo que hay que confirmar, dicho para una persona y no para un servidor. */
export interface CambioPorConfirmar {
  /** «Cambiar», «Crear o enviar», «Borrar o quitar». */
  readonly accion: string;
  /** «alumnos», «la ficha del buscador»… */
  readonly sobre: string;
  /** `PUT /quiet-hours`, para quien quiera saber exactamente qué sale. */
  readonly tecnico: string;
}

interface Pendiente extends CambioPorConfirmar {
  readonly resolver: (si: boolean) => void;
}

let pendiente: Pendiente | null = null;
/** Lo que se pinta: la pregunta sin su `resolver`, y el mismo objeto mientras dure. */
let visible: CambioPorConfirmar | null = null;
const oyentesDeCambios = new Set<() => void>();
/** Cuántos diálogos hay montados para contestar. Sin ninguno, la respuesta es no. */
let quienContesta = 0;

const avisarCambio = () => {
  for (const fn of oyentesDeCambios) fn();
};

const suscribirCambios = (fn: () => void) => {
  oyentesDeCambios.add(fn);
  return () => {
    oyentesDeCambios.delete(fn);
  };
};

export const cambioPorConfirmar = (): CambioPorConfirmar | null => visible;

export const useCambioPorConfirmar = (): CambioPorConfirmar | null =>
  useSyncExternalStore(
    suscribirCambios,
    cambioPorConfirmar,
    cambioPorConfirmar
  );

/** Lo llama el diálogo al montarse; devuelve con qué desmontarse. */
export const contestarCambios = (): (() => void) => {
  quienContesta += 1;
  return () => {
    quienContesta -= 1;
    // Desmontado con una pregunta en el aire: no se guarda nada.
    if (quienContesta === 0) responder(false);
  };
};

export const responder = (si: boolean): void => {
  const p = pendiente;
  pendiente = null;
  visible = null;
  avisarCambio();
  p?.resolver(si);
};

/**
 * Pregunta y espera. **Sin diálogo montado, no**: un cambio que nadie ha podido
 * confirmar no se manda. Una segunda pregunta mientras hay una abierta espera su
 * turno; dos a la vez en pantalla confundirían cuál se está contestando.
 */
export async function pedirConfirmacion(
  cambio: CambioPorConfirmar
): Promise<boolean> {
  if (quienContesta === 0) return false;

  while (pendiente) {
    await new Promise<void>((listo) => {
      const fuera = suscribirCambios(() => {
        if (!pendiente) {
          fuera();
          listo();
        }
      });
    });
  }

  return new Promise<boolean>((resolver) => {
    pendiente = { ...cambio, resolver };
    visible = {
      accion: cambio.accion,
      sobre: cambio.sobre,
      tecnico: cambio.tecnico,
    };
    avisarCambio();
  });
}

/* ── Decir el cambio con palabras ───────────────────────────────────────── */

/** Lo primero tras `/clubs/{club}/`, en español. Lo que no esté sale tal cual. */
const SOBRE: Readonly<Record<string, string>> = {
  students: 'alumnos',
  groups: 'grupos',
  'schedule-slots': 'horarios',
  sessions: 'clases',
  attendance: 'asistencia',
  subscriptions: 'cuotas',
  'fee-plans': 'tarifas',
  invoices: 'recibos',
  'billing-runs': 'remesas',
  payers: 'pagadores',
  mandates: 'mandatos SEPA',
  discounts: 'descuentos',
  announcements: 'avisos',
  feed: 'el muro',
  'quiet-hours': 'el horario de silencio',
  venues: 'sedes',
  disciplines: 'disciplinas',
  branding: 'la marca',
  profile: 'la ficha del buscador',
  'trial-bookings': 'clases de prueba',
  settings: 'los ajustes del club',
  memberships: 'el personal',
  staff: 'el personal',
  invitations: 'invitaciones',
  exams: 'exámenes',
  grades: 'grados',
  curriculum: 'el temario',
  competitions: 'campeonatos',
  materials: 'material',
  'data-requests': 'solicitudes de RGPD',
  consents: 'consentimientos',
  documents: 'documentos',
  guardians: 'tutores',
  enrollments: 'matrículas',
};

const ACCION: Readonly<Record<string, string>> = {
  POST: 'Crear o enviar',
  PUT: 'Cambiar',
  PATCH: 'Cambiar',
  DELETE: 'Borrar o quitar',
};

/** `/students/01a…/guardians` → «alumnos» (manda el primer tramo). */
export function describirCambio(
  metodo: string,
  ruta: string
): CambioPorConfirmar {
  const primero = ruta.split('/').filter(Boolean)[0] ?? '';
  return {
    accion: ACCION[metodo] ?? metodo,
    sobre: SOBRE[primero] ?? (primero || 'el club'),
    tecnico: `${metodo} ${ruta}`,
  };
}

/**
 * La ruta dentro del club, si la petición es a **ese** club; `null` si no.
 *
 * `/v1/clubs/{club}/students?x=1` → `/students`. Lo que va a otro sitio —el
 * back office, la sesión, las subidas— no se pregunta: no cambia datos del club, o
 * los cambia luego otra petición que sí pasa por aquí.
 */
export function rutaEnElClub(url: string, club: string): string | null {
  let camino: string;
  try {
    camino = new URL(url, 'http://x').pathname;
  } catch {
    return null;
  }

  const prefijo = `/v1/clubs/${club}`;
  if (camino !== prefijo && !camino.startsWith(`${prefijo}/`)) return null;
  return camino.slice(prefijo.length) || '/';
}

const LECTURA = new Set(['GET', 'HEAD', 'OPTIONS']);

/** La respuesta de un cambio que se canceló: un problema, como los del servidor. */
export const CODIGO_CANCELADO = 'support_change_cancelled';

const cancelado = () =>
  new Response(
    JSON.stringify({
      type: 'about:blank',
      title: 'Cambio cancelado',
      status: 400,
      detail:
        'No se ha guardado nada: lo cancelaste en la confirmación del modo soporte.',
      code: CODIGO_CANCELADO,
    }),
    { status: 400, headers: { 'content-type': 'application/problem+json' } }
  );

const metodoDe = (entrada: RequestInfo | URL, init?: RequestInit) =>
  (
    init?.method ??
    (typeof Request !== 'undefined' && entrada instanceof Request
      ? entrada.method
      : 'GET')
  ).toUpperCase();

const urlDe = (entrada: RequestInfo | URL): string =>
  typeof entrada === 'string'
    ? entrada
    : entrada instanceof URL
      ? entrada.href
      : entrada.url;

/**
 * El `fetch` del panel con la confirmación del modo soporte delante.
 *
 * Solo para: una entrada en soporte **con escritura**, un método que cambia algo, y
 * una ruta del club de esa entrada. Todo lo demás pasa sin tocar. En solo lectura
 * no se pregunta nada: el servidor ya responde `403 support_read_only`, y el
 * panel esconde los botones.
 */
export function conConfirmacionDeSoporte(fetcher: Peticion): Peticion {
  return async (entrada, init) => {
    const e = actual;
    const metodo = metodoDe(entrada, init);
    if (!e || !e.escritura || LECTURA.has(metodo)) {
      return fetcher(entrada, init);
    }

    const ruta = rutaEnElClub(urlDe(entrada), e.club);
    if (ruta === null) return fetcher(entrada, init);

    const si = await pedirConfirmacion(describirCambio(metodo, ruta));
    return si ? fetcher(entrada, init) : cancelado();
  };
}
